// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import {
  selectCartCurrency,
  updateProductAndGetCartOrNotify,
} from '@gandi/cart';
import type { DecoratedProduct } from '@gandi/cart';
import {
  selectMailboxesPrices,
  type MailboxPricesByType,
  mailboxes,
} from '@gandi/redux-resource-price';
import { useTranslator } from '@gandi/translate';
import { Switch } from '@gandi/ui';

import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';
import { useAppSelector } from 'src/hooks/useAppSelector';

import DetailRow from './DetailRow';

const YEARLY_SUBSCRIPTION = 12; // based on minDuration in months
const MONTHS_COUNT = 12;
const MAILBOX_COUNT = 2;

type Props = {
  product: DecoratedProduct,
};

export function FreeMailboxRow({ product }: Props): React.MixedElement | null {
  const { __, formatPrice } = useTranslator();
  const dispatch = useDispatch();
  const currency = useAppSelector(selectCartCurrency);

  const mailboxPriceParams = {
    process: 'create',
    productType: 'mailboxv2',
  };

  // mailbox price
  const prices: MailboxPricesByType = useAppSelector(state =>
    selectMailboxesPrices(state, mailboxPriceParams),
  );

  const isFetchingPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsFetchingOne(
      state,
      'mailboxv2_create',
    ),
  );

  const isLoadedPrices = useAppSelector(state =>
    mailboxes.priceMailboxesResource.selectIsLoadedOne(
      state,
      'mailboxv2_create',
    ),
  );

  React.useEffect(() => {
    if (!isFetchingPrices && !isLoadedPrices) {
      dispatch(fetchMailboxesPrices());
    }
  }, [dispatch, isFetchingPrices, isLoadedPrices]);

  // else we could render the row without prices
  if (!isLoadedPrices || !prices.standard_new) return null;

  const handleOnChange: (evt: SyntheticInputEvent<>) => mixed = evt => {
    const mailbox_optin = evt.target.checked;
    const newPayload = { ...product.payload, mailbox_optin };
    dispatch(updateProductAndGetCartOrNotify(__, product.id, newPayload));
  };

  const switchName = `free-mailbox-${product.id}`;

  const price =
    prices.standard_new[YEARLY_SUBSCRIPTION].priceBeforeTaxes *
    MAILBOX_COUNT *
    MONTHS_COUNT;
  const {
    payload: { mailbox_optin },
  } = product;

  const label = (
    <Switch
      checked={!!mailbox_optin}
      name={switchName}
      onChange={handleOnChange}
    >
      {__('Try our mailbox offer')}
    </Switch>
  );

  const detail = (
    <>
      <div>{__('2 mailboxes included for 1 year free of charge')}</div>
      <div>
        {__('Then %(mbPrice)s excl. VAT per month per mailbox after that.', {
          mbPrice: formatPrice(
            prices.standard_new[YEARLY_SUBSCRIPTION].priceBeforeTaxes,
            currency,
          ),
        })}
      </div>
      <div>{__('Offer is commitment-free.')}</div>
    </>
  );

  return (
    <DetailRow
      key={`domaindetails-freemailbox-${product.id}`}
      label={label}
      detail={detail}
      priceMention={mailbox_optin ? __('Free') : undefined}
      price={mailbox_optin ? price : undefined}
    />
  );
}
